/** Best-effort Android haptics synchronized to an authored non-semantic rhythm. */

export const HAPTIC_RHYTHM_DEFAULTS = Object.freeze({
  mode: "breath_bpm",
  pulseMs: 115,
  intervalMs: 1429,
  jitterMs: 28,
  maxContinuousMs: 10 * 60 * 1000,
  cooldownMs: 5 * 60 * 1000,
  maxSessionMs: 60 * 60 * 1000,
});

const clamp = (value, minimum, maximum, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(minimum, Math.min(maximum, number)) : fallback;
};

export function normalizeHapticRhythm(scene = {}) {
  const strength = clamp(scene.hapticStrength, 0, 1, 0.65);
  const rawMode = String(scene.hapticMode || HAPTIC_RHYTHM_DEFAULTS.mode).toLowerCase();
  const mode = rawMode === "breath_synced_heartbeat" || rawMode === "breath/bpm"
    ? HAPTIC_RHYTHM_DEFAULTS.mode
    : rawMode;
  const bpm = clamp(scene.hapticBpm ?? scene.heartbeatBpm ?? scene.bpm, 24, 60, 42);
  const bpmIntervalMs = 60_000 / bpm;
  const authoredIntervalMs = Number(scene.hapticIntervalMs);
  // Treat the old 1000 ms sidecar as legacy rather than letting it create a
  // second, faster rhythm. Explicit slow values remain respected.
  const intervalFallback = Number.isFinite(authoredIntervalMs) && authoredIntervalMs >= 1_200
    ? authoredIntervalMs
    : bpmIntervalMs;
  return {
    mode,
    strength,
    pulseMs: Math.round(clamp(scene.hapticPulseMs, 100, 120, 100 + strength * 20)),
    intervalMs: Math.round(clamp(intervalFallback, 1_000, 3_000, HAPTIC_RHYTHM_DEFAULTS.intervalMs)),
    jitterMs: Math.round(clamp(scene.hapticJitterMs, 0, 40, HAPTIC_RHYTHM_DEFAULTS.jitterMs)),
    maxContinuousMs: HAPTIC_RHYTHM_DEFAULTS.maxContinuousMs,
    cooldownMs: HAPTIC_RHYTHM_DEFAULTS.cooldownMs,
    maxSessionMs: HAPTIC_RHYTHM_DEFAULTS.maxSessionMs,
  };
}

export function buildHapticPulse(scene = {}, random = Math.random) {
  const rhythm = normalizeHapticRhythm(scene);
  const jitter = (Number(random()) * 2 - 1) * rhythm.jitterMs;
  const intervalMs = Math.round(clamp(
    rhythm.intervalMs + jitter,
    rhythm.intervalMs - rhythm.jitterMs,
    rhythm.intervalMs + rhythm.jitterMs,
    rhythm.intervalMs,
  ));
  return {
    mode: rhythm.mode,
    pattern: [rhythm.pulseMs, Math.max(1, intervalMs - rhythm.pulseMs)],
    pulseMs: rhythm.pulseMs,
    intervalMs,
    jitterMs: Math.round(intervalMs - rhythm.intervalMs),
  };
}
export class HapticHeartbeatController {
  constructor({ onPulse = null, now = () => Date.now(), random = Math.random } = {}) {
    this.scene = {};
    this.timer = null;
    this.desired = false;
    this.paused = false;
    this.lastAccepted = false;
    this.lastPulseAt = 0;
    this.pulseLedger = [];
    this.onPulse = typeof onPulse === "function" ? onPulse : null;
    this.clock = typeof now === "function" ? now : () => Date.now();
    this.random = typeof random === "function" ? random : Math.random;
    this.continuousStartedAt = 0;
    this.sessionExposureMs = 0;
    this.cooldownUntil = 0;
    this.wakeLock = null;
    this.wakeLockPending = null;
    this._onVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        this.cancelPattern();
        this.releaseWakeLock();
      } else if (this.desired && !this.paused) {
        this.requestWakeLock();
        this.schedulePattern();
      }
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this._onVisibilityChange);
    }
  }

  supported() {
    return typeof navigator !== "undefined" && typeof navigator.vibrate === "function";
  }

  wakeLockSupported() {
    return typeof navigator !== "undefined" && typeof navigator.wakeLock?.request === "function";
  }

  requestWakeLock() {
    if (!this.desired || !this.wakeLockSupported() || this.wakeLock || this.wakeLockPending) {
      return this.wakeLockPending;
    }
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return null;
    // A screen wake lock keeps an active Android Chrome session visible. It is
    // optional: denial, battery policy, or an unsupported browser must never
    // interrupt audio or vibration setup.
    this.wakeLockPending = navigator.wakeLock.request("screen")
      .then((sentinel) => {
        if (!this.desired || (typeof document !== "undefined" && document.visibilityState !== "visible")) {
          sentinel.release?.().catch?.(() => {});
          return null;
        }
        this.wakeLock = sentinel;
        sentinel.addEventListener?.("release", () => {
          if (this.wakeLock === sentinel) this.wakeLock = null;
        });
        return sentinel;
      })
      .catch(() => null)
      .finally(() => { this.wakeLockPending = null; });
    return this.wakeLockPending;
  }

  releaseWakeLock() {
    const sentinel = this.wakeLock;
    this.wakeLock = null;
    if (sentinel?.release) {
      try { sentinel.release().catch?.(() => {}); } catch { /* already released */ }
    }
  }

  applyScene(scene = {}) {
    this.scene = scene && typeof scene === "object" ? scene : {};
    if (this.desired && this.scene.hapticEnabled !== true) this.stop();
    else if (this.desired) this.schedulePattern();
  }

  heartbeatPattern() {
    return buildHapticPulse(this.scene, this.random).pattern;
  }

  cycleMilliseconds() {
    return buildHapticPulse(this.scene, () => 0.5).intervalMs;
  }

  _now() {
    const value = Number(this.clock());
    return Number.isFinite(value) ? value : Date.now();
  }

  _currentExposureMs(now = this._now()) {
    return this.sessionExposureMs + (this.continuousStartedAt
      ? Math.max(0, now - this.continuousStartedAt) : 0);
  }

  _pauseExposure(now = this._now()) {
    if (this.continuousStartedAt) {
      this.sessionExposureMs += Math.max(0, now - this.continuousStartedAt);
      this.continuousStartedAt = 0;
    }
  }

  _canPulse(now = this._now()) {
    const rhythm = normalizeHapticRhythm(this.scene);
    if (rhythm.mode !== HAPTIC_RHYTHM_DEFAULTS.mode) return false;
    if (this.cooldownUntil && now < this.cooldownUntil) return false;
    if (this._currentExposureMs(now) >= rhythm.maxSessionMs) return false;
    if (this.continuousStartedAt && now - this.continuousStartedAt >= rhythm.maxContinuousMs) {
      this._pauseExposure(now);
      this.paused = true;
      this.cooldownUntil = now + rhythm.cooldownMs;
      this.cancelPattern();
      this.releaseWakeLock();
      return false;
    }
    return true;
  }

  _recordPulse(event) {
    this.pulseLedger.push(event);
    if (this.pulseLedger.length > 600) this.pulseLedger.shift();
    try { this.onPulse?.(event); } catch { /* telemetry must never stop playback */ }
  }

  start(scene = {}) {
    this.applyScene(scene);
    this.desired = this.scene.hapticEnabled === true;
    this.paused = false;
    const now = this._now();
    if (!this.desired || !this.supported()) return false;
    if (this.cooldownUntil && now < this.cooldownUntil) {
      this.paused = true;
      return false;
    }
    if (this._currentExposureMs(now) >= normalizeHapticRhythm(this.scene).maxSessionMs) return false;
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return false;
    if (navigator.userActivation && !navigator.userActivation.hasBeenActive) return false;
    if (!this.continuousStartedAt) this.continuousStartedAt = now;
    this.requestWakeLock();
    return this.schedulePattern();
  }

  schedulePattern() {
    this.cancelPattern();
    if (!this.desired || this.paused || !this.supported()) return false;
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return false;
    const fire = () => {
      this.timer = null;
      const now = this._now();
      if (!this._canPulse(now)) return;
      const pulse = buildHapticPulse(this.scene, this.random);
      let accepted = false;
      try {
        accepted = navigator.vibrate(pulse.pattern) === true;
      } catch {
        accepted = false;
      }
      this.lastAccepted = accepted;
      this.lastPulseAt = now;
      this._recordPulse({
        timestamp: now,
        accepted,
        mode: pulse.mode,
        vibrate_ms: pulse.pulseMs,
        interval_ms: pulse.intervalMs,
        jitter_ms: pulse.jitterMs,
      });
      if (this.desired && !this.paused && this._canPulse(now)) {
        this.timer = globalThis.setTimeout(fire, pulse.intervalMs);
      }
    };
    fire();
    return this.lastAccepted;
  }

  cancelPattern() {
    if (this.timer !== null) globalThis.clearTimeout(this.timer);
    this.timer = null;
    if (this.supported()) {
      try { navigator.vibrate(0); } catch { /* unsupported implementation */ }
    }
  }

  stop() {
    this.desired = false;
    this.paused = false;
    this._pauseExposure();
    this.cancelPattern();
    this.releaseWakeLock();
  }

  pause() {
    this.paused = true;
    this._pauseExposure();
    this.cancelPattern();
    this.releaseWakeLock();
  }

  resume(scene = null) {
    if (scene && typeof scene === "object") this.applyScene(scene);
    const now = this._now();
    if (this.cooldownUntil && now < this.cooldownUntil) return false;
    this.paused = false;
    if (!this.desired || !this.supported()) return false;
    if (this._currentExposureMs(now) >= normalizeHapticRhythm(this.scene).maxSessionMs) return false;
    this.continuousStartedAt = now;
    this.requestWakeLock();
    return this.schedulePattern();
  }

  status() {
    const now = this._now();
    const rhythm = normalizeHapticRhythm(this.scene);
    return {
      supported: this.supported(),
      enabled: this.desired,
      paused: this.paused,
      accepted: this.lastAccepted,
      wake_lock_supported: this.wakeLockSupported(),
      wake_lock_active: Boolean(this.wakeLock && !this.wakeLock.released),
      mode: rhythm.mode,
      pulse_ms: rhythm.pulseMs,
      interval_ms: rhythm.intervalMs,
      jitter_ms: rhythm.jitterMs,
      pulse_count: this.pulseLedger.length,
      last_pulse_at: this.lastPulseAt || null,
      exposure_ms: this._currentExposureMs(now),
      cooldown_remaining_ms: Math.max(0, this.cooldownUntil - now),
      visible: typeof document === "undefined" || document.visibilityState === "visible",
    };
  }

  destroy() {
    this.stop();
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this._onVisibilityChange);
    }
  }
}
